import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useAtom, useSetAtom } from 'jotai'
import isEmpty from 'lodash-es/isEmpty'
import { useTranslation } from 'react-i18next'
import { useDebounce, useFeatureFlags } from '@node-space/hooks'
import {
  Box,
  Button,
  Combobox,
  EmptyState,
  Input,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { Lottie } from 'components/Animations/SimpleLottie'
import { ContactsSearchList } from 'components/Contacts/ContactsSearchList'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import { SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useContactInstrumentsInfiniteQuery } from 'hooks/queries/useContactInstrumentsInfiniteQuery'
import { useWalletQuery } from 'hooks/queries/useWalletQuery'
import { useWalletsQueryV2 } from 'hooks/queries/useWalletsQueryV2'
import { useGetWalletLegacyId } from 'hooks/queries/Wallets/useGetWalletLegacyId'
import { useSupportedCurrencies } from 'hooks/useSupportedCurrencies'
import {
  contactActionAtom,
  contactAtom,
  contactInstrumentActionAtom,
  contactInstrumentAtom,
} from 'pages/Contacts/atoms/contactAtoms'
import { ContactsSheets } from 'pages/Contacts/components/ContactsSheets'
import { ContactInstrumentsSheet } from 'pages/Contacts/ContactInstruments'
import { stripSpecialCharacters } from 'pages/Contacts/utils/utils'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { ContactActionIds, ContactInstrumentActionIds } from 'types/atoms/atom.contacts'
import { Contact, ContactInstrument } from 'types/contacts'
import { SendStep } from 'types/walletActions'
import { sendStepAtom, sendWalletAtom } from './atoms/atoms'

export const SendContact = () => {
  const { t } = useTranslation()
  const { enableContactBankAccounts } = useFeatureFlags()
  const { accountCapabilities } = useAccountsContext()

  const { getSupportedCurrencyWallets, getWalletOptions } = useSupportedCurrencies()
  const setContactInstrument = useSetAtom(contactInstrumentAtom)
  const setContact = useSetAtom(contactAtom)
  const setSendStep = useSetAtom(sendStepAtom)
  const [walletAction] = useContext(WalletActionContext)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchWalletQuery, setSearchWalletQuery] = useState('')
  const [walletError, setWalletError] = useState(false)
  const debouncedSearchTerm = useDebounce(searchQuery, SEARCH_INPUT_DEBOUNCE_TIME)
  const debouncedSearchWalletTerm = useDebounce(searchWalletQuery, 1000)
  const [sendWallet, setSendWallet] = useAtom(sendWalletAtom)
  const [hasLoadedMoreContacts, setHasLoadedMoreContacts] = useState(false)
  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false)
  const [isContactInstrumentSheetOpen, setIsContactInstrumentSheetOpen] = useState(false)

  const [contactAction, setContactAction] = useAtom(contactActionAtom)
  const [contactInstrumentAction, setContactInstrumentAction] = useAtom(contactInstrumentActionAtom)

  const { isEpcWalletPayoutsCapabilityEnabled } = accountCapabilities || {}

  const { data: wallets, isFetching: isFetchingWallets } = useWalletsQueryV2({
    walletType: '',
    searchTerm: debouncedSearchWalletTerm,
    max: 100,
    order: 'asc',
    sort: 'description',
    includeEmbedded: isEpcWalletPayoutsCapabilityEnabled,
    enabled: !walletAction?.meta?.customerWallet,
  })

  const { data: customerWalletLegacyId, isFetching: isFetchingCustomerWalletLegacyId } =
    useGetWalletLegacyId(walletAction?.meta?.customerWallet?.id)

  const { data: customerWallet, isFetching: isFetchingCustomerWallet } = useWalletQuery(
    customerWalletLegacyId,
    !!customerWalletLegacyId
  )

  const {
    data: contactInstruments,
    fetchNextPage,
    hasNextPage,
    isError,
    isPending,
    isFetchingNextPage,
  } = useContactInstrumentsInfiniteQuery(debouncedSearchTerm)

  const activeWallets = getSupportedCurrencyWallets(wallets?.data)

  useEffect(() => {
    if (walletAction?.meta?.customerWallet && customerWallet && !sendWallet) {
      wallets?.data?.unshift(customerWallet)
      setSendWallet(customerWallet)
      return
    }

    if (walletAction?.meta?.sourceWallet && wallets?.data && !sendWallet) {
      const sourceWallet = wallets?.data?.find(
        wallet => wallet?.lsid === walletAction?.meta?.sourceWallet?.lsid
      )

      if (!sourceWallet) wallets?.data?.unshift(walletAction?.meta?.sourceWallet)

      setSendWallet(walletAction?.meta?.sourceWallet)
    }
  }, [walletAction, wallets, customerWallet, sendWallet])

  const sourceWallets = useMemo(
    () => [
      ...(activeWallets?.filter(wallet => wallet?.balance > 0) ?? []),
      ...(customerWallet ? [customerWallet] : []),
    ],
    [activeWallets, customerWallet]
  )

  const sourceWalletOptions = useMemo(() => getWalletOptions(sourceWallets), [sourceWallets])

  const noResults = debouncedSearchTerm && !contactInstruments?.length
  const showEmptyState =
    !isPending && !isError && !contactInstruments?.length && !debouncedSearchTerm
  const showErrorState = isError && !isPending
  const showNoSearchResults = noResults && !isPending
  const showSearch = !!contactInstruments?.length || noResults

  const handleWalletOnChange = (walletId: string) => {
    const selectedWallet = sourceWallets?.find(wallet => wallet?.lsid === walletId)

    setSendWallet(selectedWallet)
    setWalletError(false)
    setSearchWalletQuery('')
  }

  const handleOpen = (isOpen: boolean) => {
    if (!isOpen) {
      setSearchWalletQuery('')
    }
  }

  const handleContactSelect = (contact: Contact, contactInstrument: ContactInstrument) => {
    if (isEmpty(sendWallet)) {
      setWalletError(true)
      return
    }
    setContact(contact)
    setContactInstrument(contactInstrument)
    setSendStep(SendStep.DETAILS)
  }

  const handleAddNewClick = () => {
    setContact(null)
    setContactInstrumentAction({
      ...contactInstrumentAction,
      actionId: ContactInstrumentActionIds?.CONTACT_INSTRUMENT_DECISION,
      onClose: () => {
        setIsContactInstrumentSheetOpen(false)
        setContact(null)
      },
    })
    setIsContactInstrumentSheetOpen(true)
  }

  const handleAddContact = () => {
    setContactAction({
      ...contactAction,
      actionId: ContactActionIds.CONTACT,
      header: t(enableContactBankAccounts ? 'contacts.addContact' : 'contacts.addCryptoContact'),
      onClose: () => {
        setIsContactSheetOpen(false)
        setContact(null)
      },
      meta: {
        enableCreateInstrument: true,
      },
    })
    setIsContactSheetOpen(true)
  }

  const isWalletDisabled = useMemo(() => {
    return !!walletAction?.meta?.customerWallet || !!walletAction?.meta?.sourceWallet
  }, [walletAction?.sourceWalletReference, walletAction?.meta?.sourceWallet])

  const isLoadingWallets = useMemo(() => {
    return isFetchingWallets || isFetchingCustomerWallet || isFetchingCustomerWalletLegacyId
  }, [isFetchingWallets, isFetchingCustomerWallet, isFetchingCustomerWalletLegacyId])

  return (
    <>
      <Box paddingT={24} className="max-h-[75vh]">
        {/* Show error state */}
        {showErrorState && (
          <EmptyState
            testId="send-contact-error"
            LottiePlayer={<Lottie src="connection-error" />}
            title={t('defaultError')}
            description={
              <>
                {`${t('requestErrors.comeBackLater')} `}
                <Button variant="ghost" noPadding onClick={() => window.location.reload()}>
                  {t('requestErrors.refreshPage')}
                </Button>
              </>
            }
          />
        )}
        {/* Show content state */}
        {!isError && (
          <>
            <Box paddingB={16}>
              <Combobox
                key={sendWallet?.lsid}
                id="from-wallet"
                testId="from-wallet"
                items={sourceWalletOptions}
                label={t('wallets.send.sendFromWallet')}
                loading={isLoadingWallets}
                loadingInline={isLoadingWallets}
                onChange={handleWalletOnChange}
                onInputChange={setSearchWalletQuery}
                defaultValue={sendWallet?.lsid ?? walletAction?.meta?.sourceWallet?.lsid}
                errorText={walletError && t('wallets.send.sendFromWalletRequired')}
                onOpen={handleOpen}
                disabled={isWalletDisabled}
                modal
                rightHeader={t('balance')}
              />
            </Box>
            {showSearch && (
              <Box paddingB={16} width="full" justify="between" flex gap={12}>
                <Box background="standard" width="full">
                  <Input
                    placeholderText={t('contacts.searchPlaceholder')}
                    onChange={event => setSearchQuery(stripSpecialCharacters(event.target.value))}
                    searchable={true}
                    testId="contact-search-field"
                  />
                </Box>
                <Button testId="add-contact" variant="secondary" onClick={handleAddNewClick}>
                  {t('contacts.addNew')}
                </Button>
              </Box>
            )}

            {isPending && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

            {showNoSearchResults && (
              <NoSearchResults
                clearSearchCriteria={() => {
                  setSearchQuery('')
                }}
              />
            )}

            {!!contactInstruments?.length && (
              <Box className="w-full">
                <ContactsSearchList
                  isInstruments
                  handleContactSelect={handleContactSelect}
                  isFetchingNextPage={isFetchingNextPage}
                  contacts={contactInstruments}
                  hasLoadedMore={hasLoadedMoreContacts}
                  fetchNextPage={fetchNextPage}
                  setHasLoadedMore={setHasLoadedMoreContacts}
                  hasNextPage={hasNextPage}
                />
              </Box>
            )}
          </>
        )}
        {/* Show empty state */}
        {showEmptyState && (
          <EmptyState
            testId="send-contact-empty"
            LottiePlayer={<Lottie src="no-data" />}
            title={t(
              enableContactBankAccounts
                ? 'contacts.noContactHeadlineV2'
                : 'contacts.noContactHeadline'
            )}
            description={t(
              enableContactBankAccounts
                ? 'contacts.addContactDescriptionV2'
                : 'contacts.addContactDescription'
            )}
            PrimaryButton={
              <Button testId="add-contact" onClick={handleAddContact}>
                {t('contacts.addContact')}
              </Button>
            }
          />
        )}
      </Box>
      <ContactsSheets isSheetOpen={isContactSheetOpen} isNestedSheet />
      <ContactInstrumentsSheet isSheetOpen={isContactInstrumentSheetOpen} isNestedSheet />
    </>
  )
}
